import { useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { selectedLinesAtom, selectedWordsAtom } from "$/states/main.ts";
import { selectedWordIdAtom } from "$/modules/spectrogram/states/dnd.ts";

interface MarqueeState {
    isSelecting: boolean;
    /** Starting mouse position relative to the scroll container */
    startClientX: number;
    startClientY: number;
    /** Current mouse position relative to the scroll container */
    currentClientX: number;
    currentClientY: number;
}

/**
 * Hook that implements marquee (lasso) drag selection in the spectrogram.
 * Click and drag on empty space to draw a region; intersecting lyric line
 * segments are selected on mouse up.
 */
export function useMarqueeSelection(
    scrollContainerRef: React.RefObject<HTMLDivElement | null>,
) {
    const setSelectedLines = useSetAtom(selectedLinesAtom);
    const setSelectedWords = useSetAtom(selectedWordsAtom);
    const setSelectedWordId = useSetAtom(selectedWordIdAtom);

    const [marquee, setMarquee] = useState<MarqueeState | null>(null);
    const marqueeRef = useRef<MarqueeState | null>(null);

    const handleMarqueeStart = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            // Only initiate on left mouse button, and only on the container itself (empty space)
            if (event.button !== 0) return;

            // Don't start marquee if clicking on a lyric segment element
            const target = event.target as HTMLElement;
            if (target.closest("[data-line-id]")) return;

            event.preventDefault();

            const container = scrollContainerRef.current;
            if (!container) return;
            const containerRect = container.getBoundingClientRect();

            const state: MarqueeState = {
                isSelecting: true,
                startClientX: event.clientX - containerRect.left,
                startClientY: event.clientY - containerRect.top,
                currentClientX: event.clientX - containerRect.left,
                currentClientY: event.clientY - containerRect.top,
            };

            marqueeRef.current = state;
            setMarquee(state);
        },
        [scrollContainerRef],
    );

    useEffect(() => {
        if (!marquee?.isSelecting) return;

        const handleMouseMove = (event: MouseEvent) => {
            const container = scrollContainerRef.current;
            if (!container || !marqueeRef.current) return;
            const containerRect = container.getBoundingClientRect();

            const updatedState: MarqueeState = {
                ...marqueeRef.current,
                currentClientX: event.clientX - containerRect.left,
                currentClientY: event.clientY - containerRect.top,
            };

            marqueeRef.current = updatedState;
            setMarquee(updatedState);
        };

        const handleMouseUp = () => {
            const container = scrollContainerRef.current;
            if (!container || !marqueeRef.current) {
                marqueeRef.current = null;
                setMarquee(null);
                return;
            }

            const m = marqueeRef.current;

            // Compute marquee box relative to the container
            const minX = Math.min(m.startClientX, m.currentClientX);
            const maxX = Math.max(m.startClientX, m.currentClientX);
            const minY = Math.min(m.startClientY, m.currentClientY);
            const maxY = Math.max(m.startClientY, m.currentClientY);

            // Only select if drag was meaningful (>5px in any direction)
            if (maxX - minX < 5 && maxY - minY < 5) {
                marqueeRef.current = null;
                setMarquee(null);
                // If it was just a click on empty space, clear the selection
                setSelectedLines(new Set());
                setSelectedWords(new Set());
                setSelectedWordId(null);
                return;
            }

            // Find all lyric word segment elements and check intersection
            const containerRect = container.getBoundingClientRect();
            const wordElements = container.querySelectorAll("[data-word-id]");
            const selectedWordIds = new Set<string>();
            const selectedLineIds = new Set<string>();

            for (const el of wordElements) {
                const wordId = (el as HTMLElement).dataset.wordId;
                const parentLineId = (el as HTMLElement).dataset.parentLineId;
                if (!wordId || !parentLineId) continue;

                const elRect = el.getBoundingClientRect();
                // Convert element rect to container-relative coordinates
                const elLeft = elRect.left - containerRect.left;
                const elRight = elRect.right - containerRect.left;
                const elTop = elRect.top - containerRect.top;
                const elBottom = elRect.bottom - containerRect.top;

                // Check if the element intersects the marquee rectangle
                if (elRight > minX && elLeft < maxX && elBottom > minY && elTop < maxY) {
                    selectedWordIds.add(wordId);
                    selectedLineIds.add(parentLineId);
                }
            }

            // Also allow selecting empty lines (no words) that intersect
            const lineElements = container.querySelectorAll("[data-line-id]");
            for (const el of lineElements) {
                const lineId = (el as HTMLElement).dataset.lineId;
                if (!lineId) continue;
                if (selectedLineIds.has(lineId)) continue; // Already selected via words

                const elRect = el.getBoundingClientRect();
                const elLeft = elRect.left - containerRect.left;
                const elRight = elRect.right - containerRect.left;
                const elTop = elRect.top - containerRect.top;
                const elBottom = elRect.bottom - containerRect.top;

                if (elRight > minX && elLeft < maxX && elBottom > minY && elTop < maxY) {
                    selectedLineIds.add(lineId);
                }
            }

            setSelectedLines(selectedLineIds);
            setSelectedWords(selectedWordIds);
            setSelectedWordId(null);

            marqueeRef.current = null;
            setMarquee(null);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp, { once: true });

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [marquee?.isSelecting, scrollContainerRef, setSelectedLines, setSelectedWords]);

    // Compute style for the marquee rectangle overlay
    let marqueeStyle: React.CSSProperties | null = null;
    if (marquee?.isSelecting) {
        const left = Math.min(marquee.startClientX, marquee.currentClientX);
        const top = Math.min(marquee.startClientY, marquee.currentClientY);
        const width = Math.abs(marquee.currentClientX - marquee.startClientX);
        const height = Math.abs(marquee.currentClientY - marquee.startClientY);

        marqueeStyle = {
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
        };
    }

    return {
        handleMarqueeStart,
        marqueeStyle,
        isMarqueeSelecting: marquee?.isSelecting ?? false,
    };
}
